import { Newspaper, Star } from "lucide-react";

const press = [
  {
    logo: "/images/logos/times-of-india.png",
    name: "The Times of India",
    tag: "India's Largest English Daily",
    quote: "BrandBorn is transforming how Indian D2C brands launch on marketplaces.",
    delay: "0s"
  },
  {
    logo: "/images/logos/economic-times.png",
    name: "The Economic Times",
    tag: "India's #1 Business Newspaper",
    quote: "The onboarding agency behind 1000+ successful marketplace launches.",
    link: "https://economictimes.indiatimes.com/ai-innovator/profile/brandborn/133204259",
    badge: "ET AI Innovator Nominee",
    delay: "0.3s"
  }
];

const FeaturedInPress = () => {
  return (
    <section className="py-14 md:py-20 px-4 bg-gray-950 relative overflow-hidden" data-testid="featured-in-press">
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-red-600/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-full text-sm font-bold mb-5">
            <Newspaper size={16} className="text-red-400" />
            AS FEATURED IN NATIONAL MEDIA
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-3">
            BrandBorn in the{" "}
            <span className="bg-gradient-to-r from-red-500 via-orange-400 to-yellow-400 bg-clip-text text-transparent">News</span>
          </h2>
          <p className="text-gray-400 text-sm md:text-base max-w-2xl mx-auto">
            India's #1 brand onboarding agency — recognized by the nation's biggest publications
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-10 max-w-4xl mx-auto" style={{ perspective: '1200px' }}>
          {press.map((item, idx) => (
            <div
              key={idx}
              data-testid={`press-card-${idx}`}
              className="press-card-3d group relative rounded-3xl p-8 bg-white/[0.06] backdrop-blur-xl border border-white/10 hover:border-white/30 transition-colors duration-500"
              style={{ animationDelay: item.delay }}
            >
              <div className="absolute -top-3 -right-3 flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-red-600 to-orange-500 rounded-full shadow-lg shadow-red-900/50">
                <Star size={12} className="text-yellow-300 fill-yellow-300" />
                <span className="text-white text-[10px] font-black tracking-wider uppercase">{item.badge || "Featured"}</span>
              </div>

              <div className="press-logo-float bg-white rounded-2xl p-3 mb-6 flex items-center justify-center h-28 shadow-2xl" style={{ animationDelay: item.delay }}>
                <img
                  src={item.logo}
                  alt={`BrandBorn featured in ${item.name}`}
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>

              <p className="text-gray-300 text-sm md:text-base italic leading-relaxed mb-4">"{item.quote}"</p>
              <div>
                <p className="text-white font-black text-base">{item.name}</p>
                <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider mt-0.5">{item.tag}</p>
              </div>
              {item.link && (
                <a
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`press-link-${idx}`}
                  className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-500 hover:to-orange-400 text-white text-xs font-black uppercase tracking-wider rounded-full shadow-lg shadow-red-900/40 transition-colors duration-300 relative z-10"
                >
                  <Newspaper size={13} />
                  Read the ET Feature →
                </a>
              )}

              <div className="press-shine absolute inset-0 rounded-3xl pointer-events-none overflow-hidden" />
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .press-card-3d {
          transform-style: preserve-3d;
          animation: pressEntrance 1s cubic-bezier(0.22, 1, 0.36, 1) both, pressFloat3d 6s ease-in-out 1s infinite;
          will-change: transform;
        }
        .press-card-3d:hover {
          animation-play-state: paused, paused;
          transform: rotateY(-6deg) rotateX(4deg) translateY(-10px) scale(1.02);
          transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
        }
        @keyframes pressEntrance {
          0% { opacity: 0; transform: rotateY(45deg) translateZ(-120px) translateY(40px); }
          100% { opacity: 1; transform: rotateY(0deg) translateZ(0) translateY(0); }
        }
        @keyframes pressFloat3d {
          0%, 100% { transform: rotateY(0deg) rotateX(0deg) translateY(0); }
          25% { transform: rotateY(3deg) rotateX(-2deg) translateY(-8px); }
          50% { transform: rotateY(0deg) rotateX(0deg) translateY(0); }
          75% { transform: rotateY(-3deg) rotateX(2deg) translateY(-6px); }
        }
        .press-logo-float {
          animation: pressLogoBob 4s ease-in-out infinite;
          transform-style: preserve-3d;
        }
        @keyframes pressLogoBob {
          0%, 100% { transform: translateZ(30px) translateY(0); }
          50% { transform: translateZ(30px) translateY(-6px); }
        }
        .press-shine::after {
          content: '';
          position: absolute;
          top: -50%;
          left: -60%;
          width: 40%;
          height: 200%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent);
          transform: rotate(20deg);
          animation: pressShimmer 5s ease-in-out infinite;
        }
        @keyframes pressShimmer {
          0%, 60% { left: -60%; }
          100% { left: 130%; }
        }
      `}</style>
    </section>
  );
};

export default FeaturedInPress;
